import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter, useLocation } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import i18n from 'i18next';
import 'bootstrap/dist/css/bootstrap.min.css';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { LoadScript } from '@react-google-maps/api';
import initAuth0 from '@/shared/components/account/auth/withAuth0';
import TimepickerStyles from '@/shared/components/form/date-pickers/timepickerStyles';
import Loading from '@/shared/components/Loading';
import WalletConnectProvider from '@/shared/components/account/auth/WalletConnect';
import { Toaster } from 'react-hot-toast';
import Router from './Router';
import store from './store';
import ScrollToTop from './ScrollToTop';
import { config as i18nextConfig } from '../../translations';
import GlobalStyles from './globalStyles';
import RechartStyles from './rechartStyles';
import NotificationStyles from './notificationStyles';
import CalendarStyles from './calendarStyles';
import AppContextProvider from '../../context';
import { AuthContext } from '../../context/AuthContext';
import { GlobalContext } from '../../context/GlobalContext';
import { PermissionContext } from '../../context/PermissionContext';
import { BookingContext } from '../../context/BookingContext';
import Footer from './Footer';

i18n.init(i18nextConfig);

// Helper to decrypt localStorage items
const decryptLocalStorageItem = (key) => {
  const encrypted = localStorage.getItem(key);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, 'T4$Bv9#Aa@QWz8m&5!Lpo1kHsTz4jL2');
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error(`Decryption error for ${key}:`, error);
    return null;
  }
};

const ThemeComponent = ({ children }) => {
  const {
 mode, direction, shadow, border, 
} = useSelector(state => ({
    mode: state.theme.className,
    direction: state.rtl.direction,
    shadow: state.shadow.className,
    border: state.border.className,
  }));

  const theme = createTheme({ palette: { type: mode } });

  const {
     setUserId, setUsername,
   setAgentId, setStatusCode, setType,
     setCurrencyType, SetCurrencyCode, statusCode, setCountryId,
  } = useContext(AuthContext);
  const { setPermissionsAccess } = useContext(PermissionContext);
  const { tripDataFlag, setTripDataFlag } = useContext(BookingContext);

  const location = useLocation();

  useEffect(() => {
    const permissions = decryptLocalStorageItem('permissions');
    if (permissions) {
      try {
        const parsed = JSON.parse(permissions);
        setPermissionsAccess(parsed);
      } catch (error) {
        console.error('Error parsing permissions:', error);
      }
    }

    const id = decryptLocalStorageItem('userid');
    const status = decryptLocalStorageItem('statuscode');
    const agent = decryptLocalStorageItem('agentid');
    const type = decryptLocalStorageItem('type');
    const name = decryptLocalStorageItem('username');
    const currency = decryptLocalStorageItem('78582826978678984898069');
    const currencyCode = decryptLocalStorageItem('99117114114101110991219599111100101');
    const countryID = decryptLocalStorageItem('cid');

    if (id) setUserId(id);
    if (status) setStatusCode(status);
    if (agent) setAgentId(agent);
    if (type) setType(type);
    if (name) setUsername(name);
    if (countryID) setCountryId(countryID);
    if (currencyCode) SetCurrencyCode(currencyCode);
    if (currency) {
      try {
        setCurrencyType(JSON.parse(currency));
      } catch (e) {
        console.error('Error parsing currencyType:', e);
        setCurrencyType('0');
      }
    } else {
      setCurrencyType('0');
    }
  }, []);

  // Reset trip meal state if not on meal route
  useEffect(() => {
    if (location.pathname !== '/agent/meal') {
      setTripDataFlag({
        ...tripDataFlag,
        tripID: '',
        name: '',
        addMealINBooking: false,
      });
    }
  }, [location]);
  

  return (
    <MuiThemeProvider theme={theme}>
      <ThemeProvider theme={{
 mode, direction, shadow, border, 
}}
      >
        <GlobalStyles />
        <NotificationStyles />
        <RechartStyles />
        <TimepickerStyles />
        <CalendarStyles />
        {children}
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

ThemeComponent.propTypes = {
  children: PropTypes.node.isRequired,
};

const ConnectedThemeComponent = ThemeComponent;

const App = () => {
  useEffect(() => {
    window.addEventListener('load', initAuth0);
  }, []);

  return (
    <AppContextProvider>
      <Provider store={store}>
        <BrowserRouter>
          <ConnectedThemeComponent>
            <LoadScript loadingElement={<Loading loading />} googleMapsApiKey="">
              <ScrollToTop>
                <Router />
                <Toaster position="top-right" reverseOrder={false} />
              </ScrollToTop>
              <Footer />
            </LoadScript>
          </ConnectedThemeComponent>
        </BrowserRouter>
      </Provider>
    </AppContextProvider>
  );
};

export default App;
